import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, Terminal, Container, ShieldCheck } from 'lucide-react';
import { FormikInput } from '../../components/ui/Input'; 
import Button from '../../components/ui/Button'; 
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import SEO from '../../components/shared/SEO';

const ForgotSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
});

export default function Forgot() {
  const [isSent, setIsSent] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState('');

  const handleReset = async (values, { setSubmitting, setFieldError }) => {
    try {
      await authAPI.forgot(values);
      setEmailSentTo(values.email);
      setIsSent(true);
    } catch (error) {
      setFieldError('email', error.response?.data?.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <SEO title="Account Recovery" 
        description="Initiate a secure password reset protocol." 
        noIndex={true} />
   
    <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white p-6 relative overflow-hidden font-sans">
      
      {/* --- Background Effects --- */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-600/10 rounded-full blur-[120px] opacity-50" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-[#0d0d0d]/80 backdrop-blur-2xl border border-white/10 p-10 rounded-[2.5rem] shadow-2xl relative z-10"
      >
        <AnimatePresence mode="wait">
          {!isSent ? (
            /* STATE 1: Input Form */
            <motion.div
              key="form"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="flex flex-col items-center mb-10">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500/10 to-red-600/10 rounded-2xl flex items-center justify-center border border-orange-500/20 mb-6">
                  <Mail className="text-orange-500" size={28} />
                </div>
                <h2 className="text-3xl font-black tracking-tighter text-white mb-2 text-center">Forgot Access?</h2>
                <p className="text-gray-500 text-sm font-medium text-center">
                    Enter your email to receive a recovery link.
                </p>
              </div>

              <Formik
                initialValues={{ email: '' }}
                validationSchema={ForgotSchema}
                onSubmit={handleReset}
              >
                {({ isSubmitting }) => (
                  <Form>
                    <FormikInput
                      name="email"
                      label="Recovery Email"
                      placeholder="you@company.com"
                      type="email"
                      autoFocus
                    />

                    <Button
                      type="submit"
                      className="w-full h-14 mt-4 bg-white text-black hover:bg-gray-200 font-black text-lg rounded-2xl transition-all shadow-xl shadow-white/5 disabled:opacity-50"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                          Sending Link...
                        </div>
                      ) : (
                        'Send Recovery Link'
                      )}
                    </Button>
                  </Form>
                )}
              </Formik>

              <div className="mt-10 pt-8 border-t border-white/5 text-center">
                <Link to="/login" className="group inline-flex items-center text-xs font-black uppercase tracking-[0.2em] text-gray-500 hover:text-white transition-colors">
                  <ArrowLeft size={14} className="mr-2 group-hover:-translate-x-1 transition-transform" /> Back to Login
                </Link>
              </div>
            </motion.div>
          ) : (
            /* STATE 2: Success Message */
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/20">
                <CheckCircle className="text-green-400" size={32} />
              </div>

              <h2 className="text-3xl font-black tracking-tighter mb-2">Check Inbox</h2>
              <p className="text-gray-500 text-sm mb-8 leading-relaxed font-medium">
                We've sent a secure recovery link to <br />
                <span className="text-white">{emailSentTo}</span>
              </p>

              <div className="bg-white/[0.03] rounded-2xl p-6 mb-8 border border-white/10 text-left">
                <div className="flex items-start gap-4">
                  <Terminal size={20} className="text-orange-500 mt-1 shrink-0" />
                  <div className="text-xs font-mono text-gray-500 leading-relaxed">
                    <p>If you don't see the email, check your spam folder or verify your connection to the EnvDock network.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Button
                  onClick={() => setIsSent(false)}
                  variant="outline"
                  className="w-full h-12 border-white/10 hover:bg-white/5 rounded-xl text-sm font-bold"
                >
                  Try another email
                </Button>

                <div className="pt-4 flex flex-col items-center gap-4">
                    <Link to="/login" className="text-xs font-black uppercase tracking-widest text-orange-500 hover:text-orange-400 transition-colors">
                      Skip, I remembered my password
                    </Link>
                    
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/5 border border-green-500/10">
                        <ShieldCheck size={14} className="text-green-500" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-green-500/80">
                            Recovery Secured
                        </span>
                    </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

     
    </div>
     </>
  );
}